/* =========================================================================
   charts.tsx — cinematic on-brand SVG charts with animated reveals.
   Components: CashflowChart, LineArea, BarSeries, Tornado, Waterfall,
               Sparkline, GaugeRing, Donut, StarsBackground.
   ========================================================================= */

import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import type {
  LineAreaProps,
  BarSeriesProps,
  WaterfallProps,
  TornadoProps,
  GaugeRingProps,
  SparklineProps,
  DonutProps,
  CashflowChartProps,
  StarsBackgroundProps,
  ChartPadding,
  Year,
  WaterfallItem,
} from "./types";
import { gaugeHurdleStatusLabel } from "./presentation/return-labels";
const CHART_TOKENS = {
  animation: { duration: 650, ease: (p: number) => 1 - Math.pow(1 - p, 3) },
  grid: {
    stroke: "hsl(var(--c-border-subtle) / 0.35)",
    strokeDasharray: "2 4" as const,
  },
  zeroLine: { stroke: "hsl(var(--c-foreground-muted) / 0.35)", strokeWidth: 1 },
  typography: {
    axisTick: {
      fontSize: 11,
      fontWeight: 500,
      fill: "hsl(var(--c-foreground-muted))",
    },
    axisTitle: {
      fontSize: 10,
      fontWeight: 600,
      fill: "hsl(var(--c-foreground-muted))",
      letterSpacing: "0.08em",
      textTransform: "uppercase" as const,
    },
    dataLabel: {
      fontSize: 11,
      fontWeight: 600,
      fill: "hsl(var(--c-foreground))",
    },
    chartTitle: {
      fontSize: 14,
      fontWeight: 600,
      fill: "hsl(var(--c-foreground))",
    },
  },
  barRadius: 2,
  tooltip: {
    bg: "hsl(var(--c-card) / 0.98)",
    border: "1px solid hsl(var(--c-border))",
    borderRadius: 8,
    padding: "8px 12px",
    boxShadow: "var(--shadow-lg)",
    fontSize: 12,
    maxWidth: 280,
    zIndex: 5,
  },
} as const;

const COLORS: Record<string, string> = {
  primary: "hsl(var(--c-chart-1))",
  sky: "hsl(var(--c-chart-2))",
  success: "hsl(var(--c-success))",
  destructive: "hsl(var(--c-destructive))",
  warning: "hsl(var(--c-warning))",
  amber: "hsl(var(--c-chart-7))",
  muted: "hsl(var(--c-foreground-muted))",
};

const WATERFALL_ACCENTS = Array.from(
  { length: 12 },
  (_, index) => `hsl(var(--c-chart-${index + 1}))`,
);

function withAlpha(color: string, alpha: number): string {
  return color.replace(/\)\s*$/, ` / ${alpha})`);
}

// ----- shared utilities -----
export function useAnimatedReveal(
  deps: React.DependencyList,
  ms: number = CHART_TOKENS.animation.duration,
): number {
  const [t, setT] = useState(0);
  const prefersReduced = useRef(false);

  useEffect(() => {
    prefersReduced.current = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
  }, []);

  useEffect(() => {
    if (prefersReduced.current) {
      setT(1);
      return;
    }
    let raf: number;
    let start: number | null = null;
    const step = (now: number) => {
      start ??= now;
      const p = Math.min(1, (now - start) / ms);
      const eased = CHART_TOKENS.animation.ease(p);
      setT(eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return t;
}

export function nice(v: number): number {
  const sign = v < 0 ? -1 : 1;
  const abs = Math.abs(v);
  const mag = Math.pow(10, Math.floor(Math.log10(abs || 1)));
  const norm = abs / mag;
  let r: number;
  if (norm < 1.5) r = 1;
  else if (norm < 3) r = 2;
  else if (norm < 7) r = 5;
  else r = 10;
  return sign * r * mag;
}

export function niceTicks(min: number, max: number, count = 5): number[] {
  if (min === max) return [min];
  const range = nice(max - min);
  const step = nice(range / (count - 1));
  const niceMin = Math.floor(min / step) * step;
  const niceMax = Math.ceil(max / step) * step;
  const out: number[] = [];
  for (let v = niceMin; v <= niceMax + 1e-9; v += step) out.push(v);
  return out;
}

export const formatTickShort = (v: number): string => {
  const abs = Math.abs(v);
  const sign = v < 0 ? "−" : "";
  if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(0)}M`;
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(0)}K`;
  return `${sign}${Math.round(abs)}`;
};

export const formatPct = (v: number): string => `${(v * 100).toFixed(0)}%`;

interface TooltipRowProps {
  label: string;
  v: string | number;
  color?: string;
  muted?: boolean;
  bold?: boolean;
}

function TooltipRow({
  label,
  v,
  color,
  muted,
  bold,
}: Readonly<TooltipRowProps>) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 18,
        padding: "2px 0",
        wordBreak: "break-word",
      }}
    >
      <span style={{ color: "hsl(var(--c-foreground-muted))", flexShrink: 0 }}>
        {label}
      </span>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontVariantNumeric: "tabular-nums",
          color:
            color ??
            (muted
              ? "hsl(var(--c-foreground-muted))"
              : "hsl(var(--c-foreground))"),
          fontWeight: bold ? 600 : 500,
          textAlign: "right",
        }}
      >
        {v}
      </span>
    </div>
  );
}

/** Shared tooltip wrapper with consistent styling and accessibility */
function TooltipContainer({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div
      style={{
        position: "absolute",
        pointerEvents: "none",
        background: CHART_TOKENS.tooltip.bg,
        border: CHART_TOKENS.tooltip.border,
        borderRadius: CHART_TOKENS.tooltip.borderRadius,
        padding: CHART_TOKENS.tooltip.padding,
        boxShadow: CHART_TOKENS.tooltip.boxShadow,
        backdropFilter: "blur(8px)",
        fontSize: CHART_TOKENS.tooltip.fontSize,
        maxWidth: CHART_TOKENS.tooltip.maxWidth,
        zIndex: CHART_TOKENS.tooltip.zIndex,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ---------------- StarsBackground ----------------
export function StarsBackground({
  count = 60,
  seed = 1,
}: Readonly<StarsBackgroundProps>) {
  const stars = useMemo(() => {
    let s = seed;
    const rand = () => {
      s = (s * 9301 + 49297) % 233280;
      return s / 233280;
    };
    return Array.from({ length: count }, () => ({
      x: rand() * 100,
      y: rand() * 100,
      r: 0.5 + rand() * 1.4,
      o: 0.2 + rand() * 0.7,
      d: rand() * 4,
    }));
  }, [count, seed]);
  return (
    <svg
      className="hero-stars"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {stars.map((s) => (
        <circle
          key={`star-${s.x.toFixed(3)}-${s.y.toFixed(3)}`}
          className="star-twinkle"
          cx={s.x}
          cy={s.y}
          r={s.r / 8}
          fill="white"
          style={
            {
              "--star-o": s.o,
              "--star-dur": `${3 + s.d}s`,
            } as React.CSSProperties
          }
        />
      ))}
    </svg>
  );
}

// helper: align two y-ranges so their zero coincides
function alignZeroRanges(
  loA: number,
  hiA: number,
  loB: number,
  hiB: number,
): [[number, number], [number, number]] {
  loA = Math.min(loA, 0);
  hiA = Math.max(hiA, 0);
  loB = Math.min(loB, 0);
  hiB = Math.max(hiB, 0);
  const fracAbove = (lo: number, hi: number) =>
    hi - lo === 0 ? 0.5 : hi / (hi - lo);
  const fA = fracAbove(loA, hiA);
  const fB = fracAbove(loB, hiB);
  let f = Math.max(fA, fB);
  f = Math.min(0.95, Math.max(0.05, f));
  const adjust = (lo: number, hi: number): [number, number] => {
    if (hi === 0 && lo === 0) return [-1, 1];
    if (Math.abs(fracAbove(lo, hi) - f) < 1e-9) return [lo, hi];
    if (fracAbove(lo, hi) < f) {
      const newHi = lo === 0 ? Math.max(hi, 1) : (-f * lo) / (1 - f);
      return [lo, newHi];
    }
    const newLo = hi === 0 ? Math.min(lo, -1) : hi - hi / f;
    return [newLo, hi];
  };
  return [adjust(loA, hiA), adjust(loB, hiB)];
}

// ---------------- CashflowTooltip ----------------
interface CashflowTooltipProps {
  year: Year;
  x: number;
  width: number;
  yFormat: (v: number) => string;
}

function CashflowTooltip({
  year: y,
  x,
  width,
  yFormat,
}: Readonly<CashflowTooltipProps>) {
  const isRight = x > width / 2;
  const xPct = (x / width) * 100;
  return (
    <TooltipContainer
      style={{
        left: isRight ? "auto" : `calc(${xPct}% + 16px)`,
        right: isRight ? `calc(${100 - xPct}% + 16px)` : "auto",
        top: 8,
        minWidth: 200,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 8,
        }}
      >
        <span
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          Year {y.year}
        </span>
        <span
          style={{
            fontSize: 10,
            color: "hsl(var(--c-foreground-muted))",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          {(y.utilisation * 100).toFixed(0)}% utilisation
        </span>
      </div>
      <TooltipRow label="Revenue" v={yFormat(y.totalRevenue)} muted />
      <TooltipRow label="Rent (gross)" v={`−${yFormat(y.rentGross)}`} muted />
      <TooltipRow
        label="Op costs"
        v={`−${yFormat(y.variableCosts + y.fixedOpex + y.fitoutAmortisation)}`}
        muted
      />
      <div
        style={{
          height: 1,
          background: "hsl(var(--c-border-subtle))",
          margin: "6px 0",
        }}
      ></div>
      <TooltipRow
        label="EBITDA"
        v={yFormat(y.reddamEbitda)}
        color="hsl(var(--c-chart-2))"
        bold
      />
      <TooltipRow
        label="Cumulative"
        v={yFormat(y.cumulativeCashflow)}
        color={y.cumulativeCashflow >= 0 ? COLORS.success : COLORS.warning}
        bold
      />
    </TooltipContainer>
  );
}

// ---------------- CashflowChart (annual bars + cumulative line + payback marker) ----------------
export function CashflowChart({
  years,
  openingBalance = 0,
  width = 760,
  height = 320,
  yFormat = formatTickShort,
  paybackYear = null,
}: Readonly<CashflowChartProps>) {
  const t = useAnimatedReveal([years, openingBalance]);
  const [hover, setHover] = useState<{
    idx: number;
    x: number;
    y: number;
  } | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);

  const {
    pad,
    yL,
    y0,
    yR,
    xBand,
    xCenter,
    linePts,
    paybackX,
    rightTicks,
    ticksL,
  } = useMemo(() => {
    const pad = { l: 64, r: 70, t: 40, b: 32 };
    const innerW = width - pad.l - pad.r;
    const innerH = height - pad.t - pad.b;

    const cums = [openingBalance, ...years.map((y) => y.cumulativeCashflow)];
    const ebs = years.map((y) => y.reddamEbitda);
    const [[loL, hiL], [, hiR]] = alignZeroRanges(
      Math.min(...cums),
      Math.max(...cums),
      Math.min(...ebs),
      Math.max(...ebs),
    );

    const ticksL = niceTicks(loL, hiL, 5);
    const yLoL = ticksL[0],
      yHiL = ticksL.at(-1) ?? 0;
    const yL = (v: number) =>
      pad.t + innerH - ((v - yLoL) / Math.max(1e-9, yHiL - yLoL)) * innerH;
    const y0 = yL(0);
    const aboveFrac = yHiL - yLoL > 1e-9 ? yHiL / (yHiL - yLoL) : 0.5;
    const yHiR_eff = hiR === 0 ? Math.max(1, ...ebs.map(Math.abs)) * 0.6 : hiR;
    const yLoR_eff =
      aboveFrac > 0 ? (-yHiR_eff * (1 - aboveFrac)) / aboveFrac : -1;
    const yR = (v: number) =>
      pad.t +
      innerH -
      ((v - yLoR_eff) / Math.max(1e-9, yHiR_eff - yLoR_eff)) * innerH;
    const rightTicks = ticksL.map((tk) => {
      const yPos = yL(tk);
      const v =
        yLoR_eff + ((yHiR_eff - yLoR_eff) * (pad.t + innerH - yPos)) / innerH;
      return { y: yPos, v };
    });

    const xBand = innerW / Math.max(1, years.length);
    const xCenter = (i: number) => pad.l + xBand * (i + 0.5);

    const linePts = [
      { x: pad.l, y: yL(openingBalance) },
      ...years.map((y, i) => ({ x: xCenter(i), y: yL(y.cumulativeCashflow) })),
    ];

    let paybackX: number | null = null;
    for (let i = 0; i < years.length; i++) {
      const prev = i === 0 ? openingBalance : years[i - 1].cumulativeCashflow;
      const cur = years[i].cumulativeCashflow;
      if (prev < 0 && cur >= 0) {
        const frac = -prev / (cur - prev);
        const x0 = i === 0 ? pad.l : xCenter(i - 1);
        const x1 = xCenter(i);
        paybackX = x0 + frac * (x1 - x0);
        break;
      }
    }

    return {
      pad,
      yL,
      y0,
      yR,
      xBand,
      xCenter,
      linePts,
      paybackX,
      rightTicks,
      ticksL,
    };
  }, [years, openingBalance, width, height]);

  const visible = Math.min(
    linePts.length,
    Math.max(2, Math.floor(linePts.length * t)),
  );
  const linePath = linePts
    .slice(0, visible)
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(" ");
  const lastPt = linePts[visible - 1] ?? linePts[0];
  const areaPath =
    linePath +
    ` L ${lastPt.x.toFixed(2)} ${y0.toFixed(2)} L ${linePts[0].x.toFixed(2)} ${y0.toFixed(2)} Z`;

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const mx = ((e.clientX - rect.left) / rect.width) * width;
    const my = ((e.clientY - rect.top) / rect.height) * height;
    if (mx < pad.l || mx > width - pad.r || my < pad.t || my > height - pad.b) {
      setHover(null);
      return;
    }
    const idx = Math.max(
      0,
      Math.min(years.length - 1, Math.floor((mx - pad.l) / xBand)),
    );
    setHover({ idx, x: xCenter(idx), y: yL(years[idx].cumulativeCashflow) });
  };
  const onLeave = () => setHover(null);

  return (
    <div style={{ position: "relative" }} tabIndex={0} aria-live="polite">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        style={{
          width: "100%",
          height: "auto",
          maxHeight: height,
          display: "block",
        }}
        role="img"
        aria-label="Cashflow chart"
        onMouseMove={onMove}
        onMouseLeave={onLeave}
      >
        <title>Cashflow chart</title>
        <desc>Visual representation of cashflow over the lease term</desc>
        <defs>
          <linearGradient id="cf-cum-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={COLORS.primary} stopOpacity="0.28" />
            <stop offset="100%" stopColor={COLORS.primary} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Grid */}
        <g className="chart-grid">
          {ticksL.map((tk) => (
            <line
              key={`grid-${tk}`}
              x1={pad.l}
              x2={width - pad.r}
              y1={yL(tk)}
              y2={yL(tk)}
              stroke="hsl(var(--c-border-subtle) / 0.35)"
              strokeDasharray="2 3"
            />
          ))}
        </g>

        {/* Zero / break-even line */}
        <line
          x1={pad.l}
          x2={width - pad.r}
          y1={y0}
          y2={y0}
          stroke="hsl(var(--c-foreground-muted) / 0.7)"
          strokeWidth="1"
        />
        <text
          x={pad.l + 6}
          y={y0 - 4}
          fontSize="10"
          fill="hsl(var(--c-foreground-muted))"
          style={{ letterSpacing: "0.06em", textTransform: "uppercase" }}
        >
          Break-even
        </text>

        {/* Left axis — cumulative cash, color-coded amber */}
        <g className="chart-axis">
          {ticksL.map((tk) => (
            <text
              key={`ltick-${tk}`}
              x={pad.l - 8}
              y={yL(tk) + 4}
              textAnchor="end"
              fill={COLORS.primary}
            >
              {yFormat(tk)}
            </text>
          ))}
          <text
            x={pad.l - 8}
            y={pad.t - 12}
            textAnchor="end"
            fontSize="10"
            fill={COLORS.primary}
            style={{
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            Cumulative
          </text>
        </g>

        {/* Right axis — annual EBITDA, color-coded blue */}
        <g className="chart-axis">
          {rightTicks.map((rt) => (
            <text
              key={`rtick-${rt.v.toFixed(2)}`}
              x={width - pad.r + 8}
              y={rt.y + 4}
              textAnchor="start"
              fill={COLORS.sky}
            >
              {yFormat(rt.v)}
            </text>
          ))}
          <text
            x={width - pad.r + 8}
            y={pad.t - 12}
            textAnchor="start"
            fontSize="10"
            fill={COLORS.sky}
            style={{
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            Annual EBITDA
          </text>
        </g>

        {/* X axis */}
        <g className="chart-axis">
          {years.map((y, i) => {
            const step = years.length > 12 ? Math.ceil(years.length / 8) : 1;
            if (i % step !== 0) return null;
            return (
              <text
                key={`yr-${y.year}`}
                x={xCenter(i)}
                y={height - 10}
                textAnchor="middle"
              >
                Y{y.year}
              </text>
            );
          })}
        </g>

        {/* Annual EBITDA bars on right scale */}
        {years.map((y, i) => {
          const top = y.reddamEbitda >= 0 ? yR(y.reddamEbitda) : y0;
          const h = Math.abs(yR(y.reddamEbitda) - y0) * t;
          const w = Math.max(3, xBand * 0.55);
          const x = xCenter(i) - w / 2;
          return (
            <rect
              key={`bar-${y.year}`}
              x={x}
              y={top}
              width={w}
              height={h}
              fill={
                y.reddamEbitda >= 0
                  ? withAlpha(COLORS.sky, 0.6)
                  : withAlpha(COLORS.destructive, 0.6)
              }
              rx="1.5"
            />
          );
        })}

        {/* Cumulative line on left scale */}
        <path d={areaPath} fill="url(#cf-cum-fill)" />
        <path
          d={linePath}
          stroke={COLORS.primary}
          strokeWidth="2"
          fill="none"
        />

        {/* Payback marker */}
        {paybackX !== null && (
          <g>
            <line
              x1={paybackX}
              x2={paybackX}
              y1={pad.t + 18}
              y2={height - pad.b}
              stroke="hsl(var(--c-success))"
              strokeWidth="1"
              strokeDasharray="3 3"
            />
            <rect
              x={paybackX - 40}
              y={pad.t}
              width="80"
              height="18"
              rx="3"
              fill="hsl(var(--c-success) / 0.18)"
              stroke="hsl(var(--c-success) / 0.5)"
            />
            <text
              x={paybackX}
              y={pad.t + 12}
              textAnchor="middle"
              fontSize="10"
              fill="hsl(var(--c-success))"
              fontWeight="600"
              style={{ letterSpacing: "0.08em", textTransform: "uppercase" }}
            >
              Payback Y{paybackYear || "—"}
            </text>
          </g>
        )}

        {/* Hover scrubber + dots */}
        {hover && years[hover.idx]
          ? (() => {
              const y = years[hover.idx];
              return (
                <g pointerEvents="none">
                  <line
                    x1={hover.x}
                    x2={hover.x}
                    y1={pad.t}
                    y2={height - pad.b}
                    stroke="hsl(var(--c-foreground) / 0.35)"
                    strokeWidth="1"
                    strokeDasharray="2 3"
                  />
                  <circle
                    cx={hover.x}
                    cy={yL(y.cumulativeCashflow)}
                    r="5"
                    fill={COLORS.primary}
                    stroke="hsl(var(--c-card))"
                    strokeWidth="2"
                  />
                  <circle
                    cx={hover.x}
                    cy={yR(y.reddamEbitda)}
                    r="4"
                    fill={COLORS.sky}
                    stroke="hsl(var(--c-card))"
                    strokeWidth="2"
                  />
                </g>
              );
            })()
          : null}
      </svg>

      {hover && years[hover.idx] ? (
        <CashflowTooltip
          year={years[hover.idx]}
          x={hover.x}
          width={width}
          yFormat={yFormat}
        />
      ) : null}
    </div>
  );
}

// ---------------- LineArea ----------------
export function LineArea({
  data,
  width = 760,
  height = 220,
  accent = "primary",
  label = "",
  yFormat = formatTickShort,
  padding,
  showZero = true,
  overlay = [],
}: Readonly<LineAreaProps>) {
  const t = useAnimatedReveal([data]);
  const [hover, setHover] = useState<{
    idx: number;
    x: number;
    y: number;
  } | null>(null);
  const gradId = useId().replace(/:/g, "-");

  const { pad, innerW, xScale, yScale, ticks, yLo, yHi, accentColor } =
    useMemo(() => {
      const pad: Required<ChartPadding> = {
        l: 50,
        r: 16,
        t: 14,
        b: 26,
        ...padding,
      };
      const innerW = width - pad.l - pad.r;
      const innerH = height - pad.t - pad.b;

      const xs = data.map((d) => d.x);
      const ys = data.map((d) => d.y);
      const allYs = [...ys, ...overlay.flatMap((o) => o.data.map((d) => d.y))];
      const yMin = Math.min(...allYs, showZero ? 0 : Infinity);
      const yMax = Math.max(...allYs, 0);
      const ticks = niceTicks(yMin, yMax, 5);
      const yLo = ticks[0];
      const yHi = ticks.at(-1) ?? 0;

      const xMin = Math.min(...xs);
      const xMax = Math.max(...xs);
      const xRange = Math.max(1, xMax - xMin);
      const xScale = (x: number) => pad.l + (innerW * (x - xMin)) / xRange;
      const yScale = (y: number) =>
        pad.t + innerH - ((y - yLo) / Math.max(1e-9, yHi - yLo)) * innerH;

      const accentColor = COLORS[accent] ?? COLORS.primary;

      return {
        pad,
        innerW,
        innerH,
        xScale,
        yScale,
        ticks,
        yLo,
        yHi,
        accentColor,
      };
    }, [data, width, height, accent, padding, showZero, overlay]);

  if (data.length === 0) return null;

  const visible = Math.max(1, Math.floor(data.length * t));
  const path = data
    .slice(0, visible)
    .map(
      (d, i) =>
        `${i === 0 ? "M" : "L"} ${xScale(d.x).toFixed(2)} ${yScale(d.y).toFixed(2)}`,
    )
    .join(" ");
  const area =
    path +
    ` L ${xScale(data[visible - 1]?.x ?? 0).toFixed(2)} ${yScale(yLo).toFixed(2)} L ${xScale(data[0].x).toFixed(2)} ${yScale(yLo).toFixed(2)} Z`;

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const mx = ((e.clientX - rect.left) / rect.width) * width;
    const my = ((e.clientY - rect.top) / rect.height) * height;
    if (mx < pad.l || mx > width - pad.r || my < pad.t || my > height - pad.b) {
      setHover(null);
      return;
    }
    const idx = Math.max(
      0,
      Math.min(
        data.length - 1,
        Math.floor((mx - pad.l) / (innerW / data.length)),
      ),
    );
    setHover({ idx, x: xScale(data[idx].x), y: yScale(data[idx].y) });
  };
  const onLeave = () => setHover(null);

  return (
    <div style={{ position: "relative" }} tabIndex={0} aria-live="polite">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="chart-wrap"
        style={{ width: "100%", height: "auto", maxHeight: height }}
        role="img"
        aria-label="Line chart"
        onMouseMove={onMove}
        onMouseLeave={onLeave}
      >
        <title>Line chart</title>
        <desc>Visual representation of a data series over time</desc>
        <defs>
          <linearGradient id={`la-fill-${gradId}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={accentColor} stopOpacity="0.35" />
            <stop offset="100%" stopColor={accentColor} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* grid + y axis */}
        <g className="chart-grid">
          {ticks.map((tk) => (
            <line
              key={`grid-${tk}`}
              x1={pad.l}
              x2={width - pad.r}
              y1={yScale(tk)}
              y2={yScale(tk)}
              stroke="hsl(var(--c-border-subtle) / 0.35)"
              strokeDasharray="2 3"
            />
          ))}
        </g>
        <g className="chart-axis">
          {ticks.map((tk) => (
            <text
              key={`tick-${tk}`}
              x={pad.l - 8}
              y={yScale(tk) + 4}
              textAnchor="end"
            >
              {yFormat(tk)}
            </text>
          ))}
          {data.map((d, i) =>
            i % Math.max(1, Math.ceil(data.length / 8)) === 0 ? (
              <text
                key={`x-${d.x}`}
                x={xScale(d.x)}
                y={height - 8}
                textAnchor="middle"
              >
                Y{d.x}
              </text>
            ) : null,
          )}
        </g>

        {/* zero line */}
        {yLo < 0 && yHi > 0 ? (
          <line
            x1={pad.l}
            x2={width - pad.r}
            y1={yScale(0)}
            y2={yScale(0)}
            stroke="hsl(var(--c-foreground-muted) / 0.5)"
            strokeWidth="1"
          />
        ) : null}

        {/* overlay dashed lines */}
        {overlay.map((ov) => {
          const ovPath = ov.data
            .map(
              (d, i) =>
                `${i === 0 ? "M" : "L"} ${xScale(d.x).toFixed(2)} ${yScale(d.y).toFixed(2)}`,
            )
            .join(" ");
          return (
            <g
              key={`ov-${ov.label}`}
              opacity={hover ? 0.35 : 0.6}
              style={{ transition: "opacity 0.2s" }}
            >
              <path
                d={ovPath}
                stroke={ov.color || "hsl(var(--c-foreground-muted))"}
                strokeWidth="1.5"
                fill="none"
                strokeDasharray={ov.dasharray || "4 3"}
              />
            </g>
          );
        })}

        {/* area */}
        <path
          d={area}
          fill={`url(#la-fill-${gradId})`}
          opacity={hover ? 0.6 : 1}
          style={{ transition: "opacity 0.2s" }}
        />
        <path
          d={path}
          stroke={accentColor}
          strokeWidth="2"
          fill="none"
          opacity={hover ? 0.7 : 1}
          style={{ transition: "opacity 0.2s" }}
        />

        {/* end dot */}
        {data[visible - 1] ? (
          <g>
            <circle
              cx={xScale(data[visible - 1].x)}
              cy={yScale(data[visible - 1].y)}
              r="4"
              fill={accentColor}
            />
            <circle
              cx={xScale(data[visible - 1].x)}
              cy={yScale(data[visible - 1].y)}
              r="9"
              fill={accentColor}
              opacity="0.18"
            />
          </g>
        ) : null}

        {/* hover crosshair + dot */}
        {hover ? (
          <g pointerEvents="none">
            <line
              x1={hover.x}
              x2={hover.x}
              y1={pad.t}
              y2={height - pad.b}
              stroke="hsl(var(--c-foreground-muted))"
              strokeWidth="1"
              strokeDasharray="3 3"
              opacity="0.6"
            />
            <circle
              cx={hover.x}
              cy={hover.y}
              r="4"
              fill={accentColor}
              stroke="hsl(var(--c-card))"
              strokeWidth="1.5"
            />
          </g>
        ) : null}
      </svg>

      {/* hover tooltip */}
      {hover && data[hover.idx] ? (
        <TooltipContainer
          style={{
            left: (hover.x / width) * 100 + "%",
            top: (hover.y / height) * 100 + "%",
            transform: `translateX(${hover.x > width * 0.7 ? "-100%" : hover.x < width * 0.3 ? "0%" : "-50%"}) translateY(-120%)`,
            whiteSpace: "nowrap",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 2, fontSize: 13 }}>
            Y{data[hover.idx].x}
          </div>
          <div style={{ color: "hsl(var(--c-foreground-muted))" }}>
            {label || "Value"}: {yFormat(data[hover.idx].y)}
          </div>
          {overlay.map((ov) => {
            const d = ov.data[hover.idx];
            if (!d) return null;
            return (
              <div
                key={`ovt-${ov.label}`}
                style={{ color: ov.color, marginTop: 2 }}
              >
                {ov.label}: {yFormat(d.y)}
              </div>
            );
          })}
        </TooltipContainer>
      ) : null}
    </div>
  );
}

// ---------------- BarSeries (grouped) ----------------
export function BarSeries({
  data,
  series,
  width = 760,
  height = 240,
  yFormat = formatTickShort,
  showLegend = true,
  overlays = [],
}: Readonly<BarSeriesProps>) {
  const [hover, setHover] = useState<{
    idx: number;
    sx: number;
    x: number;
    y: number;
  } | null>(null);
  const t = useAnimatedReveal([data]);

  const { pad, yScale, groupW, barW, gutter, overlayScales, ticks, yLo, yHi } =
    useMemo(() => {
      // Each active overlay gets its own staggered right-axis column so their tick labels don't pile up.
      const pad = {
        l: 56,
        r: overlays.length > 0 ? 14 + overlays.length * 44 : 16,
        t: 18,
        b: 28,
      };
      const innerW = width - pad.l - pad.r;
      const innerH = height - pad.t - pad.b;

      const allValues = data.flatMap((d) =>
        series.map((s) => Number(d[s.key] ?? 0)),
      );
      const yMin = Math.min(0, ...allValues);
      const yMax = Math.max(0, ...allValues);
      const ticks = niceTicks(yMin, yMax, 5);
      const yLo = ticks[0],
        yHi = ticks.at(-1) ?? 0;
      const yScale = (y: number) =>
        pad.t + innerH - ((y - yLo) / Math.max(1e-9, yHi - yLo)) * innerH;

      const groupW = innerW / Math.max(1, data.length);
      // The gutter between groups scales down (rather than staying a fixed 8px)
      // so a narrow container (many years/series at a small viewport) doesn't
      // let a fixed inset outrun the group's own width. barW itself is floored
      // at 1px so a <rect width> can never go negative or NaN — see D3.
      const gutter = Math.min(8, groupW * 0.25);
      const barW = Math.max(1, (groupW - gutter) / Math.max(1, series.length));

      // Compute overlay scales (each overlay has its own right-hand axis)
      const overlayScales = overlays.map((ov) => {
        const ovMin = Math.min(0, ...ov.data);
        const ovMax = Math.max(0, ...ov.data);
        const ovTicks = niceTicks(ovMin, ovMax, 3);
        const ovLo = ovTicks[0];
        const ovHi = ovTicks.at(-1) ?? 0;
        // Share the PRIMARY axis zero: value 0 maps to yScale(0), so every overlay's baseline lines up with
        // the bars' zero. Positive values scale into the space above zero; negatives into the space below.
        const zeroY = yScale(0);
        const topSpan = Math.max(1e-9, zeroY - pad.t);
        const botSpan = Math.max(1e-9, pad.t + innerH - zeroY);
        const scale = (v: number) =>
          v >= 0
            ? zeroY - (ovHi > 0 ? v / ovHi : 0) * topSpan
            : zeroY + (ovLo < 0 ? v / ovLo : 0) * botSpan;
        return { ...ov, scale, ovLo, ovHi };
      });

      return {
        pad,
        yScale,
        groupW,
        barW,
        gutter,
        overlayScales,
        ticks,
        yLo,
        yHi,
      };
    }, [data, series, width, height, overlays]);

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const mx = ((e.clientX - rect.left) / rect.width) * width;
    const my = ((e.clientY - rect.top) / rect.height) * height;
    if (mx < pad.l || mx > width - pad.r || my < pad.t || my > height - pad.b) {
      setHover(null);
      return;
    }
    const idx = Math.max(
      0,
      Math.min(data.length - 1, Math.floor((mx - pad.l) / groupW)),
    );
    const sx = Math.max(
      0,
      Math.min(
        series.length - 1,
        Math.floor((mx - pad.l - groupW * idx - gutter / 2) / barW),
      ),
    );
    setHover({ idx, sx, x: pad.l + groupW * (idx + 0.5), y: my });
  };
  const onLeave = () => setHover(null);

  const xCenter = (i: number) => pad.l + groupW * (i + 0.5);

  return (
    <div style={{ position: "relative" }} tabIndex={0} aria-live="polite">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: "100%", height: "auto", maxHeight: height }}
        className="chart-wrap"
        role="img"
        aria-label="Bar chart"
        onMouseMove={onMove}
        onMouseLeave={onLeave}
      >
        <title>Bar chart</title>
        <desc>Visual representation of categorical data</desc>
        <g className="chart-grid">
          {ticks.map((tk) => (
            <line
              key={`grid-${tk}`}
              x1={pad.l}
              x2={width - pad.r}
              y1={yScale(tk)}
              y2={yScale(tk)}
              stroke="hsl(var(--c-border-subtle) / 0.35)"
              strokeDasharray="2 3"
            />
          ))}
        </g>
        <g className="chart-axis">
          {ticks.map((tk) => (
            <text
              key={`tick-${tk}`}
              x={pad.l - 8}
              y={yScale(tk) + 4}
              textAnchor="end"
            >
              {yFormat(tk)}
            </text>
          ))}
          {(() => {
            const xStep =
              data.length > 15 ? Math.max(1, Math.ceil(data.length / 12)) : 1;
            return data.map((d, i) =>
              i % xStep === 0 || i === data.length - 1 ? (
                <text
                  key={`xlbl-${d.label}`}
                  x={pad.l + groupW * (i + 0.5)}
                  y={height - 8}
                  textAnchor="middle"
                >
                  {d.label}
                </text>
              ) : null,
            );
          })()}
        </g>
        {/* Overlay right axes — one staggered column per overlay (series named by colour in the legend),
            all sharing the primary zero baseline so a label can't land on another's. */}
        {overlayScales.map((ov, oi) => {
          const colX = width - pad.r + 12 + oi * 44;
          return (
            <g key={`ov-axis-${ov.key}`}>
              <line
                x1={colX}
                x2={colX}
                y1={pad.t}
                y2={height - pad.b}
                stroke={ov.color}
                strokeWidth="1"
                opacity="0.4"
              />
              {[ov.ovLo, (ov.ovLo + ov.ovHi) / 2, ov.ovHi].map((tk, i) => (
                <text
                  key={`ov-tick-${oi}-${i}`}
                  x={colX + 3}
                  y={ov.scale(tk) + 4}
                  textAnchor="start"
                  fill={ov.color}
                  fontSize="9"
                  opacity="0.85"
                >
                  {(ov.yFormat ?? formatTickShort)(tk)}
                </text>
              ))}
            </g>
          );
        })}
        {/* zero line */}
        {yLo < 0 && yHi > 0 ? (
          <line
            x1={pad.l}
            x2={width - pad.r}
            y1={yScale(0)}
            y2={yScale(0)}
            stroke="hsl(var(--c-foreground-muted) / 0.5)"
            strokeWidth="1"
          />
        ) : null}
        {/* bars */}
        {data.map((d, i) =>
          series.map((s, si) => {
            const val = Number(d[s.key] ?? 0);
            const y0 = yScale(0);
            const y1 = yScale(val);
            const h = Math.abs(y1 - y0) * t;
            const top = val >= 0 ? y0 - h : y0;
            const x = pad.l + groupW * i + gutter / 2 + si * barW;
            return (
              <rect
                key={`${d.label}-${s.key}`}
                x={x}
                y={top}
                width={Math.max(1, barW - 2)}
                height={Math.max(0, h)}
                fill={s.color}
                rx="2"
                opacity={hover?.idx === i && hover?.sx === si ? 0.85 : 1}
              />
            );
          }),
        )}
        {/* overlay lines */}
        {overlayScales.map((ov) => {
          const path = ov.data
            .map(
              (v, i) =>
                `${i === 0 ? "M" : "L"} ${xCenter(i).toFixed(2)} ${ov.scale(v).toFixed(2)}`,
            )
            .join(" ");
          return (
            <g key={`ov-line-${ov.key}`}>
              <path
                d={path}
                fill="none"
                stroke={ov.color}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {ov.data.map((v, i) => (
                <circle
                  key={`ov-dot-${ov.key}-${i}`}
                  cx={xCenter(i)}
                  cy={ov.scale(v)}
                  r="3"
                  fill={ov.color}
                />
              ))}
            </g>
          );
        })}
      </svg>
      {hover && data[hover.idx] && series[hover.sx]
        ? (() => {
            const d = data[hover.idx];
            const s = series[hover.sx];
            const isRight = hover.x > width / 2;
            const xPct = (hover.x / width) * 100;
            return (
              <TooltipContainer
                style={{
                  left: isRight ? "auto" : `calc(${xPct}% + 12px)`,
                  right: isRight ? `calc(${100 - xPct}% + 12px)` : "auto",
                  top: 8,
                  minWidth: 160,
                }}
              >
                <div
                  style={{
                    fontFamily: "var(--font-display)",
                    fontSize: 13,
                    fontWeight: 600,
                    marginBottom: 4,
                  }}
                >
                  {d.label}
                </div>
                <TooltipRow
                  label={s.label}
                  v={yFormat(Number(d[s.key] ?? 0))}
                  color={s.color}
                />
                {overlays
                  .filter((ov) => ov.data[hover.idx] != null)
                  .map((ov) => (
                    <TooltipRow
                      key={ov.key}
                      label={ov.label}
                      v={(ov.yFormat ?? formatTickShort)(ov.data[hover.idx])}
                      color={ov.color}
                    />
                  ))}
              </TooltipContainer>
            );
          })()
        : null}
      {showLegend ? (
        <div
          className="row gap-4"
          style={{
            paddingLeft: pad.l,
            marginTop: 4,
            fontSize: 12,
            color: "hsl(var(--c-foreground-muted))",
          }}
        >
          {series.map((s) => (
            <div key={s.key} className="row gap-2">
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: s.color,
                }}
              ></span>
              {s.label}
            </div>
          ))}
          {overlays.map((ov) => (
            <div key={ov.key} className="row gap-2">
              <span
                style={{
                  width: 10,
                  height: 3,
                  borderRadius: 1,
                  background: ov.color,
                }}
              ></span>
              {ov.label}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ---------------- Tornado ----------------
export function Tornado({
  rows,
  base,
  width = 760,
  rowHeight = 28,
  valueFormat = formatTickShort,
}: Readonly<TornadoProps>) {
  const [hover, setHover] = useState<{ idx: number; y: number } | null>(null);
  const t = useAnimatedReveal([rows, base]);

  const { pad, height, xScale, ticks } = useMemo(() => {
    const pad = { l: 180, r: 60, t: 12, b: 32 };
    const innerW = width - pad.l - pad.r;
    const height = pad.t + pad.b + rowHeight * rows.length;

    const maxAbs = Math.max(
      ...rows.map((r) =>
        Math.max(Math.abs(r.high - base), Math.abs(r.low - base)),
      ),
      1,
    );
    const ticks = niceTicks(-maxAbs, maxAbs, 5);
    const xScale = (v: number) =>
      pad.l + ((v + maxAbs) / (2 * maxAbs)) * innerW;

    return { pad, height, xScale, ticks };
  }, [rows, base, width, rowHeight]);

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const my = ((e.clientY - rect.top) / rect.height) * height;
    if (my < pad.t || my > height - pad.b) {
      setHover(null);
      return;
    }
    const idx = Math.max(
      0,
      Math.min(rows.length - 1, Math.floor((my - pad.t) / rowHeight)),
    );
    setHover({ idx, y: pad.t + idx * rowHeight + rowHeight / 2 });
  };
  const onLeave = () => setHover(null);

  return (
    <div style={{ position: "relative" }} tabIndex={0} aria-live="polite">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: "100%", height: "auto", maxHeight: height }}
        className="chart-wrap"
        data-chart="tornado"
        role="img"
        aria-label="Tornado chart"
        onMouseMove={onMove}
        onMouseLeave={onLeave}
      >
        <title>Tornado chart</title>
        <desc>Visual representation of sensitivity driver impacts</desc>
        <g className="chart-grid">
          {ticks.map((tk) => (
            <line
              key={`grid-${tk}`}
              x1={xScale(tk)}
              x2={xScale(tk)}
              y1={pad.t}
              y2={pad.t + rows.length * rowHeight}
              stroke="hsl(var(--c-border-subtle) / 0.35)"
              strokeDasharray="2 3"
            />
          ))}
        </g>
        <line
          x1={xScale(0)}
          x2={xScale(0)}
          y1={pad.t - 4}
          y2={pad.t + rows.length * rowHeight + 4}
          stroke="hsl(var(--c-foreground-muted))"
          strokeWidth="1.5"
        />

        {rows.map((r, i) => {
          const yMid = pad.t + i * rowHeight + rowHeight / 2;
          // P7-R1 (#14/#15): encode by OUTCOME — worse (lower KPI) red/left, better (higher) green/right —
          // not by flex direction, so cost drivers whose +flex worsens the return are no longer drawn green.
          const worse = Math.min(r.low, r.high);
          const better = Math.max(r.low, r.high);
          const lW = Math.abs(xScale(worse - base) - xScale(0)) * t;
          const hW = Math.abs(xScale(better - base) - xScale(0)) * t;
          const isHover = hover?.idx === i;
          return (
            <g
              key={r.variable}
              opacity={hover && !isHover ? 0.4 : 1}
              style={{ transition: "opacity 0.15s" }}
            >
              <text
                x={pad.l - 12}
                y={yMid + 4}
                textAnchor="end"
                fontSize="12"
                fill="hsl(var(--c-foreground))"
              >
                {r.label}
              </text>
              <rect
                x={xScale(0) - lW}
                y={yMid - 9}
                width={lW}
                height={18}
                fill="hsl(var(--c-destructive) / 0.55)"
                rx="2"
              />
              <rect
                x={xScale(0)}
                y={yMid - 9}
                width={hW}
                height={18}
                fill="hsl(var(--c-success) / 0.6)"
                rx="2"
              />
              <text
                x={xScale(0) - lW - 6}
                y={yMid + 4}
                textAnchor="end"
                fontSize="11"
                fill="hsl(var(--c-foreground-muted))"
              >
                {valueFormat(worse)}
              </text>
              <text
                x={xScale(0) + hW + 6}
                y={yMid + 4}
                textAnchor="start"
                fontSize="11"
                fill="hsl(var(--c-foreground-muted))"
              >
                {valueFormat(better)}
              </text>
            </g>
          );
        })}

        <g className="chart-axis">
          {ticks.map((tk) => (
            <text
              key={`tick-${tk}`}
              x={xScale(tk)}
              y={height - 12}
              textAnchor="middle"
            >
              {(tk > 0 ? "+" : tk < 0 ? "−" : "") + valueFormat(Math.abs(tk))}
            </text>
          ))}
        </g>
      </svg>
      {hover && rows[hover.idx]
        ? (() => {
            const r = rows[hover.idx];
            return (
              <TooltipContainer
                style={{
                  left: pad.l,
                  right: pad.r,
                  top: (hover.y / height) * 100 + "%",
                  transform: "translateY(-110%)",
                }}
              >
                <div
                  style={{
                    fontFamily: "var(--font-display)",
                    fontSize: 13,
                    fontWeight: 600,
                    marginBottom: 6,
                  }}
                >
                  {r.label}
                </div>
                <TooltipRow
                  label="Worse"
                  v={valueFormat(Math.min(r.low, r.high))}
                  color={COLORS.destructive}
                />
                <TooltipRow
                  label="Base"
                  v={valueFormat(base)}
                  color={"hsl(var(--c-foreground-muted))"}
                />
                <TooltipRow
                  label="Better"
                  v={valueFormat(Math.max(r.low, r.high))}
                  color={COLORS.success}
                />
              </TooltipContainer>
            );
          })()
        : null}
    </div>
  );
}

// ---------------- WaterfallTooltip ----------------

/**
 * loop#013: waterfall captions were painted word-by-word into neighbouring columns
 * (e.g. "SPECIALIST LEARNING" over "CONSTRUCTION"). Words that fit the column wrap onto
 * two lines; anything longer truncates with an ellipsis and keeps the full name as a
 * hover title (the tooltip carries the breakdown anyway). An empty label renders no
 * caption at all.
 */
export function waterfallCaptionLines(
  label: string,
  barW: number,
): { lines: string[]; truncated: boolean } {
  // ~6px per uppercase glyph at fontSize 10; barW+6 is the full column pitch.
  const colChars = Math.max(4, Math.floor((barW + 6) / 6));
  const words = label.split(" ").filter((w) => w.length > 0);
  if (words.length === 0) {
    return { lines: [], truncated: false };
  }
  if (words.length <= 2 && words.every((w) => w.length <= colChars)) {
    return { lines: words, truncated: false };
  }
  const joined = words.join(" ");
  return {
    lines: [`${joined.slice(0, Math.max(1, colChars - 1)).trimEnd()}…`],
    truncated: true,
  };
}

interface WaterfallTooltipProps {
  hover: { idx: number; x: number; y: number };
  items: WaterfallItem[];
  totalIdx: number;
  proceedsIdx: number | null;
  total?: number;
  proceeds?: number;
  width: number;
  height: number;
  valueFormat: (v: number) => string;
}

function WaterfallTooltip({
  hover,
  items,
  totalIdx,
  proceedsIdx,
  total,
  proceeds,
  width,
  height,
  valueFormat,
}: Readonly<WaterfallTooltipProps>) {
  const totalVal = items.reduce((a, b) => a + b.value, 0);
  const isTotal = hover.idx === totalIdx;
  const isProceeds = proceedsIdx !== null && hover.idx === proceedsIdx;
  let item: { label: string; value: number } | undefined;
  if (isProceeds) {
    item = { label: "Net realisation", value: proceeds ?? 0 };
  } else if (isTotal) {
    item = { label: "Total", value: total ?? totalVal };
  } else {
    item = items[hover.idx];
  }
  if (!item) return null;

  const isRight = hover.x > width / 2;
  const xPct = (hover.x / width) * 100;
  const yPct = (hover.y / height) * 100;
  let running = 0;
  for (let i = 0; i < hover.idx && i < items.length; i++)
    running += items[i].value;
  const profit =
    proceeds !== null && proceeds !== undefined ? proceeds - totalVal : null;

  const profitRow =
    isProceeds && profit !== null ? (
      <TooltipRow
        label="Profit"
        v={valueFormat(profit)}
        color={
          profit >= 0 ? "hsl(var(--c-success))" : "hsl(var(--c-destructive))"
        }
      />
    ) : null;
  const runningRow =
    isTotal || isProceeds ? null : (
      <TooltipRow
        label="Running total"
        v={valueFormat(running + item.value)}
        color={"hsl(var(--c-foreground-muted))"}
      />
    );

  return (
    <TooltipContainer
      style={{
        // loop#013: right-half hovers anchor flush to the wrapper's right edge
        // (extending leftward), so the panel can never overflow the card's RIGHT edge
        // the way the Net-realisation tooltip did. (Vertical overflow below the card is
        // a separate pre-existing behaviour — the panel overlays, not clips.)
        left: isRight ? "auto" : `calc(${xPct}% + 12px)`,
        right: isRight ? "12px" : "auto",
        top: `${Math.min(82, Math.max(4, yPct))}%`,
        minWidth: 140,
        maxWidth: 320,
        boxShadow: CHART_TOKENS.tooltip.boxShadow,
        backdropFilter: "blur(8px)",
        fontSize: 12,
        zIndex: 5,
      }}
    >
      <div
        style={{
          fontFamily: "var(--font-display)",
          fontSize: 13,
          fontWeight: 600,
          marginBottom: 4,
        }}
      >
        {item.label}
      </div>
      <TooltipRow label="Value" v={valueFormat(item.value)} />
      {profitRow}
      {isProceeds ? (
        <div
          style={{
            borderTop: CHART_TOKENS.tooltip.border,
            marginTop: 4,
            paddingTop: 4,
          }}
        >
          <div
            style={{
              fontSize: 10,
              color: "hsl(var(--c-foreground-muted))",
              marginBottom: 2,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Cost breakdown
          </div>
          {items.map((it) => (
            <TooltipRow
              key={`cost-${it.label}`}
              label={it.label}
              v={valueFormat(it.value)}
              muted
            />
          ))}
        </div>
      ) : null}
      {runningRow}
    </TooltipContainer>
  );
}

// ---------------- Waterfall (cost stack + proceeds) ----------------
export function Waterfall({
  items,
  total,
  proceeds,
  width = 760,
  height = 300,
  valueFormat = formatTickShort,
}: Readonly<WaterfallProps>) {
  const [hover, setHover] = useState<{
    idx: number;
    x: number;
    y: number;
  } | null>(null);
  const t = useAnimatedReveal([items, total, proceeds]);

  const { pad, innerW, totalSum, yScale, colCount, barW, ticks } =
    useMemo(() => {
      const pad = { l: 56, r: 16, t: 16, b: 30 };
      const innerW = width - pad.l - pad.r;
      const innerH = height - pad.t - pad.b;
      const totalSum = items.reduce((a, b) => a + b.value, 0);
      const yMax = Math.max(totalSum, total ?? 0, proceeds ?? 0) * 1.15;
      const yMin =
        proceeds !== null && proceeds !== undefined
          ? Math.min(0, proceeds - totalSum) * 1.1
          : 0;
      const ticks = niceTicks(yMin, yMax, 5);
      const yLo = ticks[0],
        yHi = ticks.at(-1) ?? 0;
      const yScale = (y: number) =>
        pad.t + innerH - ((y - yLo) / Math.max(1e-9, yHi - yLo)) * innerH;

      const colCount =
        items.length + (proceeds !== null && proceeds !== undefined ? 2 : 1);
      // Floored so a narrow container / large item count can never drive a
      // <rect width> negative or NaN — same defensive pattern as BarSeries (D3).
      const barW = Math.max(1, innerW / colCount - 6);

      return { pad, innerW, totalSum, yScale, colCount, barW, ticks };
    }, [items, total, proceeds, width, height]);

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const mx = ((e.clientX - rect.left) / rect.width) * width;
    const my = ((e.clientY - rect.top) / rect.height) * height;
    if (mx < pad.l || mx > width - pad.r || my < pad.t || my > height - pad.b) {
      setHover(null);
      return;
    }
    const colW = innerW / colCount;
    const idx = Math.max(
      0,
      Math.min(colCount - 1, Math.floor((mx - pad.l) / colW)),
    );
    setHover({ idx, x: pad.l + colW * (idx + 0.5), y: my });
  };
  const onLeave = () => setHover(null);

  const totalIdx = items.length;
  const proceedsIdx =
    proceeds !== null && proceeds !== undefined ? items.length + 1 : null;

  return (
    <div style={{ position: "relative" }} tabIndex={0} aria-live="polite">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: "100%", height: "auto", maxHeight: height }}
        className="chart-wrap"
        role="img"
        aria-label="Waterfall chart"
        onMouseMove={onMove}
        onMouseLeave={onLeave}
      >
        <title>Waterfall chart</title>
        <desc>Visual representation of cumulative changes</desc>
        <g className="chart-grid">
          {ticks.map((tk) => (
            <line
              key={`grid-${tk}`}
              x1={pad.l}
              x2={width - pad.r}
              y1={yScale(tk)}
              y2={yScale(tk)}
              stroke="hsl(var(--c-border-subtle) / 0.35)"
              strokeDasharray="2 3"
            />
          ))}
        </g>
        <g className="chart-axis">
          {ticks.map((tk) => (
            <text
              key={`tick-${tk}`}
              x={pad.l - 8}
              y={yScale(tk) + 4}
              textAnchor="end"
            >
              {valueFormat(tk)}
            </text>
          ))}
        </g>
        {(() => {
          let running = 0;
          return items.map((it, i) => {
            const prev = running;
            running += it.value;
            const x = pad.l + (innerW / colCount) * i + 6;
            const top = yScale(running);
            const h = (yScale(prev) - top) * t;
            const isHover = hover?.idx === i;
            return (
              <g
                key={it.label}
                opacity={hover !== null && !isHover ? 0.35 : 1}
                style={{ transition: "opacity 0.15s" }}
              >
                <rect
                  x={x}
                  y={yScale(prev) - h}
                  width={barW}
                  height={Math.max(0, h)}
                  fill={WATERFALL_ACCENTS[i % WATERFALL_ACCENTS.length]}
                  rx="3"
                />
                <text
                  x={x + barW / 2}
                  y={height - 12}
                  textAnchor="middle"
                  fontSize="10"
                  fill="hsl(var(--c-foreground-muted))"
                  style={{
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  <title>{it.label}</title>
                  {(() => {
                    // loop#013: tidy captions — fit-in-column words wrap onto two
                    // lines; anything longer truncates instead of painting over
                    // neighbours. Empty labels render no caption.
                    const { lines } = waterfallCaptionLines(it.label, barW);
                    return lines.map((line, wi) => (
                      <tspan
                        key={`${line}-${wi}`}
                        x={x + barW / 2}
                        dy={wi === 0 ? -6 : 12}
                      >
                        {line}
                      </tspan>
                    ));
                  })()}
                </text>
                <text
                  x={x + barW / 2}
                  y={yScale(prev) - h - 6}
                  textAnchor="middle"
                  fontSize="11"
                  fill="hsl(var(--c-foreground))"
                >
                  {valueFormat(it.value)}
                </text>
                {i < items.length - 1 ? (
                  <line
                    x1={x + barW}
                    y1={yScale(running)}
                    x2={x + barW + 6}
                    y2={yScale(running)}
                    stroke="hsl(var(--c-border))"
                    strokeDasharray="2 3"
                  />
                ) : null}
              </g>
            );
          });
        })()}
        {/* Total bar */}
        {(() => {
          const totalVal = total ?? totalSum;
          const x = pad.l + (innerW / colCount) * totalIdx + 6;
          const h = (yScale(0) - yScale(totalVal)) * t;
          const isHover = hover?.idx === totalIdx;
          return (
            <g
              opacity={hover !== null && !isHover ? 0.35 : 1}
              style={{ transition: "opacity 0.15s" }}
            >
              <rect
                x={x}
                y={yScale(0) - h}
                width={barW}
                height={Math.max(0, h)}
                fill="hsl(var(--c-primary) / 0.18)"
                stroke="hsl(var(--c-primary))"
                strokeWidth="1"
                rx="3"
              />
              <text
                x={x + barW / 2}
                y={height - 12}
                textAnchor="middle"
                fontSize="10"
                fill="hsl(var(--c-primary))"
                style={{
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  fontWeight: 600,
                }}
              >
                Total
              </text>
              <text
                x={x + barW / 2}
                y={yScale(totalVal) - 6}
                textAnchor="middle"
                fontSize="12"
                fill="hsl(var(--c-foreground))"
                fontWeight="600"
              >
                {valueFormat(totalVal)}
              </text>
            </g>
          );
        })()}
        {/* Proceeds bar + profit bridge with stacked cost segments */}
        {proceeds !== null && proceeds !== undefined
          ? (() => {
              const totalVal = total ?? totalSum;
              const x = pad.l + (innerW / colCount) * (proceedsIdx ?? 0) + 6;
              const profit = proceeds - totalVal;
              const profitY = yScale(proceeds);
              const isHover = hover?.idx === proceedsIdx;
              const profitColor =
                profit >= 0 ? COLORS.success : COLORS.destructive;
              // stacked cost segments inside the proceeds bar
              const costBase = yScale(0); // bottom of proceeds bar = zero line
              const stackSegments: {
                y: number;
                h: number;
                color: string;
                label: string;
                value: number;
              }[] = [];
              let stackRunning = 0;
              items.forEach((it, i) => {
                const segTop = yScale(stackRunning + it.value);
                const segBot = yScale(stackRunning);
                const segH = Math.max(0, (segBot - segTop) * t);
                stackSegments.push({
                  y: segBot - segH,
                  h: segH,
                  color: WATERFALL_ACCENTS[i % WATERFALL_ACCENTS.length],
                  label: it.label,
                  value: it.value,
                });
                stackRunning += it.value;
              });
              return (
                <g
                  opacity={hover !== null && !isHover ? 0.35 : 1}
                  style={{ transition: "opacity 0.15s" }}
                >
                  {/* connector line from total to proceeds */}
                  <line
                    x1={pad.l + (innerW / colCount) * totalIdx + 6 + barW + 3}
                    y1={yScale(totalVal)}
                    x2={x - 3}
                    y2={yScale(totalVal)}
                    stroke="hsl(var(--c-foreground-muted))"
                    strokeWidth="1"
                    strokeDasharray="4 3"
                  />
                  {/* full proceeds outline */}
                  <rect
                    x={x}
                    y={profitY}
                    width={barW}
                    height={Math.max(0, costBase - profitY)}
                    fill={profitColor}
                    opacity="0.08"
                    rx="3"
                  />
                  <rect
                    x={x}
                    y={profitY}
                    width={barW}
                    height={Math.max(0, costBase - profitY)}
                    fill="none"
                    stroke={profitColor}
                    strokeWidth="1"
                    rx="3"
                  />
                  {/* stacked cost segments inside — each has a native tooltip */}
                  {stackSegments.map((seg) => (
                    <rect
                      key={`stack-${seg.label}`}
                      x={x + 2}
                      y={seg.y}
                      width={Math.max(1, barW - 4)}
                      height={seg.h}
                      fill={seg.color}
                      opacity="0.45"
                      rx="1"
                      style={{ cursor: "pointer" }}
                    >
                      <title>{`${seg.label}: ${valueFormat(seg.value)}`}</title>
                    </rect>
                  ))}
                  {/* cost total divider line */}
                  <line
                    x1={x}
                    y1={yScale(totalVal)}
                    x2={x + barW}
                    y2={yScale(totalVal)}
                    stroke="hsl(var(--c-foreground))"
                    strokeWidth="1"
                    strokeDasharray="3 2"
                    opacity="0.4"
                  />
                  {/* profit zone label */}
                  {profit > 0 ? (
                    <text
                      x={x + barW / 2}
                      y={(profitY + yScale(totalVal)) / 2 + 4}
                      textAnchor="middle"
                      fontSize="10"
                      fill={profitColor}
                      fontWeight="600"
                    >
                      {valueFormat(profit)}
                    </text>
                  ) : null}
                  <text
                    x={x + barW / 2}
                    y={height - 12}
                    textAnchor="middle"
                    fontSize="10"
                    fill={profitColor}
                    style={{
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      fontWeight: 600,
                    }}
                  >
                    Net realisation
                  </text>
                  <text
                    x={x + barW / 2}
                    y={profitY - 10}
                    textAnchor="middle"
                    fontSize="11"
                    fill="hsl(var(--c-foreground))"
                    fontWeight="600"
                  >
                    {valueFormat(proceeds)}
                  </text>
                </g>
              );
            })()
          : null}
      </svg>
      {hover ? (
        <WaterfallTooltip
          hover={hover}
          items={items}
          totalIdx={totalIdx}
          proceedsIdx={proceedsIdx}
          total={total}
          proceeds={proceeds}
          width={width}
          height={height}
          valueFormat={valueFormat}
        />
      ) : null}
    </div>
  );
}

// ---------------- Sparkline ----------------
export function Sparkline({
  values,
  accent = "primary",
  height = 36,
  valueFormat = formatTickShort,
}: Readonly<SparklineProps>) {
  const [hover, setHover] = useState<{
    idx: number;
    x: number;
    y: number;
  } | null>(null);
  if (!values || values.length === 0) return null;

  const { w, h, xs, ys, d, color, min, max } = useMemo(() => {
    const min = Math.min(...values, 0);
    const max = Math.max(...values, 0);
    const w = 120,
      h = height;
    const xs = (i: number) =>
      values.length > 1 ? (w * i) / (values.length - 1) : w / 2;
    const ys = (v: number) =>
      h - 6 - ((v - min) / Math.max(1, max - min)) * (h - 12);
    const d = values
      .map((v, i) => `${i ? "L" : "M"} ${xs(i).toFixed(1)} ${ys(v).toFixed(1)}`)
      .join(" ");
    const color =
      accent === "sky"
        ? COLORS.sky
        : accent === "success"
          ? COLORS.success
          : COLORS.primary;
    return { w, h, xs, ys, d, color, min, max };
  }, [values, accent, height]);

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const mx = ((e.clientX - rect.left) / rect.width) * w;
    const idx = Math.max(
      0,
      Math.min(values.length - 1, Math.round((mx / w) * (values.length - 1))),
    );
    setHover({ idx, x: xs(idx), y: ys(values[idx]) });
  };
  const onLeave = () => setHover(null);

  return (
    <div
      style={{
        position: "relative",
        display: "inline-block",
        width: "100%",
        overflow: "hidden",
        borderRadius: "inherit",
      }}
    >
      <svg
        className="spark"
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="Sparkline"
        style={{ width: "100%", height: "100%", overflow: "hidden" }}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
      >
        <title>Sparkline</title>
        <desc>Compact visual representation of a data trend</desc>
        {/* zero reference line */}
        {min < 0 && max > 0 ? (
          <line
            x1={0}
            x2={w}
            y1={ys(0)}
            y2={ys(0)}
            stroke="hsl(var(--c-foreground-muted) / 0.25)"
            strokeWidth="1"
          />
        ) : null}
        <path d={d + ` L ${w} ${h} L 0 ${h} Z`} fill={color} opacity="0.18" />
        <path d={d} stroke={color} strokeWidth="1.5" fill="none" />
        {/* start / end dots */}
        <circle
          cx={xs(0)}
          cy={ys(values[0])}
          r="2.5"
          fill={color}
          opacity="0.7"
        />
        <circle
          cx={xs(values.length - 1)}
          cy={ys(values[values.length - 1])}
          r="2.5"
          fill={color}
          opacity="0.7"
        />
        {hover ? (
          <g pointerEvents="none">
            <circle
              cx={hover.x}
              cy={hover.y}
              r="3"
              fill={color}
              stroke="hsl(var(--c-card))"
              strokeWidth="1.5"
            />
          </g>
        ) : null}
      </svg>
      {hover ? (
        <TooltipContainer
          style={{
            left: (hover.x / w) * 100 + "%",
            top: -4,
            transform: `translateX(${hover.x > w * 0.7 ? "-100%" : hover.x < w * 0.3 ? "0%" : "-50%"}) translateY(-100%)`,
            whiteSpace: "nowrap",
            padding: "4px 8px",
          }}
        >
          <span style={{ fontFamily: "var(--font-mono)" }}>
            {valueFormat(values[hover.idx])}
          </span>
        </TooltipContainer>
      ) : null}
    </div>
  );
}

// ---------------- GaugeRing helpers ----------------
function gaugeStatusColor(
  met: boolean,
  target: number | null | undefined,
  v: number,
): string {
  if (met) return "hsl(var(--c-success))";
  if (target !== null && target !== undefined && v >= target - 0.005)
    return "hsl(var(--c-warning))";
  return "hsl(var(--c-destructive))";
}

// ---------------- GaugeRing ----------------
export function GaugeRing({
  value,
  target,
  size = 130,
  label = "IRR",
  thickness = 10,
  valueFormat = formatPct,
}: Readonly<GaugeRingProps>) {
  const t = useAnimatedReveal([value, target]);
  const v = Number.isFinite(value) ? value : 0;
  const tgt =
    target != null && Number.isFinite(target) && target > 0 ? target : 0;
  const met = v >= tgt;
  const gradId = useId().replace(/:/g, "-");

  const {
    valFrac,
    cx,
    cy,
    r,
    circ,
    nx1,
    ny1,
    nx2,
    ny2,
    lx,
    ly,
    arcStart,
    arcEnd,
  } = useMemo(() => {
    const scale = Math.max(tgt * 2, Math.abs(v) * 1.25, 0.001);
    const valFrac = Math.max(0, Math.min(1, v / scale));
    const tgtFrac = tgt > 0 ? Math.min(0.995, tgt / scale) : 0;

    const cx = size / 2,
      cy = size / 2;
    const r = (size - thickness) / 2;
    const circ = 2 * Math.PI * r;

    const ang = tgtFrac * 2 * Math.PI - Math.PI / 2;
    const innerR = r - thickness / 2 - 1;
    const outerR = r + thickness / 2 + 4;
    const nx1 = cx + Math.cos(ang) * innerR,
      ny1 = cy + Math.sin(ang) * innerR;
    const nx2 = cx + Math.cos(ang) * outerR,
      ny2 = cy + Math.sin(ang) * outerR;
    const calloutOffset = Math.max(20, size * 0.12);
    const lx = cx + Math.cos(ang) * (outerR + calloutOffset);
    const ly = cy + Math.sin(ang) * (outerR + calloutOffset);

    const arcStart = met ? COLORS.success : COLORS.warning;
    const arcEnd = met ? COLORS.success : COLORS.primary;

    return {
      valFrac,
      cx,
      cy,
      r,
      circ,
      nx1,
      ny1,
      nx2,
      ny2,
      lx,
      ly,
      arcStart,
      arcEnd,
    };
  }, [v, tgt, met, size, thickness]);

  const sweep = valFrac * circ * t;

  return (
    <svg
      viewBox={`${-size * 0.15} ${-size * 0.15} ${size * 1.3} ${size * 1.3}`}
      style={{ width: size, height: size, overflow: "visible" }}
      role="img"
      aria-label="Gauge ring"
      tabIndex={0}
    >
      <title>Gauge ring</title>
      <desc>Visual representation of a value against a target</desc>
      <defs>
        <linearGradient id={`gauge-grad-${gradId}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={arcStart} />
          <stop offset="100%" stopColor={arcEnd} />
        </linearGradient>
      </defs>
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke="hsl(var(--c-muted))"
        strokeWidth={thickness}
      />
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke={`url(#gauge-grad-${gradId})`}
        strokeWidth={thickness}
        strokeLinecap="round"
        strokeDasharray={`${sweep} ${circ}`}
        transform={`rotate(-90 ${cx} ${cy})`}
      />

      {/* Hurdle notch */}
      {tgt > 0 ? (
        <g style={{ opacity: t }}>
          <line
            x1={nx1}
            y1={ny1}
            x2={nx2}
            y2={ny2}
            stroke="hsl(var(--c-foreground))"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle
            cx={lx}
            cy={ly}
            r={Math.max(13, size * 0.1)}
            fill="hsl(var(--c-card-elevated))"
            stroke="hsl(var(--c-foreground) / 0.45)"
            strokeWidth="1"
          />
          <text
            x={lx}
            y={ly}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={Math.max(9, size * 0.068)}
            fill="hsl(var(--c-foreground))"
            fontWeight="600"
            style={{ fontFamily: "var(--font-mono)" }}
          >
            {valueFormat(tgt)}
          </text>
        </g>
      ) : null}

      {/* Center labels */}
      <text
        x={cx}
        y={cy - 4}
        textAnchor="middle"
        fontSize={size * 0.17}
        fontWeight="600"
        fill="hsl(var(--c-foreground))"
        style={{ fontFamily: "var(--font-display)" }}
      >
        {valueFormat(v)}
      </text>
      <text
        x={cx}
        y={cy + size * 0.11}
        textAnchor="middle"
        fontSize={size * 0.078}
        fill="hsl(var(--c-foreground-muted))"
        style={{ letterSpacing: "0.12em", textTransform: "uppercase" }}
      >
        {label}
      </text>
      <text
        x={cx}
        y={cy + size * 0.22}
        textAnchor="middle"
        fontSize={size * 0.07}
        fill={gaugeStatusColor(met, target, v)}
        fontWeight="600"
        style={{ letterSpacing: "0.10em", textTransform: "uppercase" }}
      >
        {gaugeHurdleStatusLabel(met, target, v)}
      </text>
    </svg>
  );
}

// ---------------- Donut ----------------
export function Donut({
  parts,
  size = 130,
  thickness = 18,
  total,
  valueFormat = formatTickShort,
}: Readonly<DonutProps>) {
  const [hover, setHover] = useState<{ idx: number } | null>(null);
  const t = useAnimatedReveal([parts]);
  const { sum, cx, cy, r, circ } = useMemo(() => {
    const sum = parts.reduce((a, b) => a + b.value, 0);
    const cx = size / 2,
      cy = size / 2;
    const r = (size - thickness) / 2;
    const circ = 2 * Math.PI * r;
    return { sum, cx, cy, r, circ };
  }, [parts, size, thickness]);
  let offset = 0;

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const mx = ((e.clientX - rect.left) / rect.width) * size - cx;
    const my = ((e.clientY - rect.top) / rect.height) * size - cy;
    let ang = Math.atan2(my, mx) + Math.PI / 2;
    if (ang < 0) ang += 2 * Math.PI;
    const frac = ang / (2 * Math.PI);
    let acc = 0;
    for (let i = 0; i < parts.length; i++) {
      acc += parts[i].value / Math.max(1, sum);
      if (frac <= acc) {
        setHover({ idx: i });
        return;
      }
    }
    setHover(null);
  };
  const onLeave = () => setHover(null);

  return (
    <div
      style={{ position: "relative", display: "inline-block" }}
      tabIndex={0}
      aria-live="polite"
    >
      <svg
        viewBox={`0 0 ${size} ${size}`}
        style={{ width: size, height: size }}
        role="img"
        aria-label="Donut chart"
        onMouseMove={onMove}
        onMouseLeave={onLeave}
      >
        <title>Donut chart</title>
        <desc>Visual representation of proportional data</desc>
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="hsl(var(--c-muted))"
          strokeWidth={thickness}
        />
        {parts.map((p, i) => {
          const len = (p.value / Math.max(1, sum)) * circ * t;
          const dash = `${len} ${circ - len}`;
          const isHover = hover?.idx === i;
          const el = (
            <circle
              key={p.label}
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={p.color}
              strokeWidth={isHover ? thickness + 4 : thickness}
              strokeDasharray={dash}
              strokeDashoffset={-offset}
              transform={`rotate(-90 ${cx} ${cy})`}
              opacity={hover && !isHover ? 0.45 : 1}
              style={{ transition: "opacity 0.15s, stroke-width 0.15s" }}
            />
          );
          offset += len;
          return el;
        })}
        <text
          x={cx}
          y={cy - 2}
          textAnchor="middle"
          fontSize="20"
          fontWeight="700"
          fill="hsl(var(--c-foreground))"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {total !== null && total !== undefined
            ? valueFormat(total)
            : valueFormat(sum)}
        </text>
      </svg>
      {hover && parts[hover.idx]
        ? (() => {
            const p = parts[hover.idx];
            const pct = ((p.value / Math.max(1, sum)) * 100).toFixed(1) + "%";
            return (
              <TooltipContainer
                style={{
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  textAlign: "center",
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 2 }}>
                  {p.label}
                </div>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>
                  {valueFormat(p.value)} · {pct}
                </div>
              </TooltipContainer>
            );
          })()
        : null}
    </div>
  );
}
